import { cn } from '@/lib/utils';

/**
 * A single horizontal percentage bar, matching the PDF report's
 * `percentage-bar.ts`: a faint full-width track, a solid fill to `fraction`,
 * a bold percent label overlaid at the left of the bar, optional right-aligned
 * caption (e.g. "267 avg. kcal"), and optional AMDR reference brackets.
 */
export type PercentBarProps = {
  /** Bold label left of the bar. */
  label: string;
  /** Small muted sublabel under the label (e.g. a clock range). */
  sublabel?: string;
  /** Fill fraction, 0–1. Clamped for rendering. */
  fraction: number;
  /** Bar colour (hex). The track is this colour at ~10% opacity. */
  colour: string;
  /** Right-aligned caption inside the bar row. */
  rightText?: string;
  /** AMDR reference band as [min, max] fractions — draws corner brackets. */
  amdr?: [number, number];
  className?: string;
};

export const PercentBar = ({
  label,
  sublabel,
  fraction,
  colour,
  rightText,
  amdr,
  className,
}: PercentBarProps) => {
  const pct = Math.max(0, Math.min(1, fraction));
  // Hex + "1A" alpha suffix ≈ 10% opacity for the track.
  const track = `${colour}1A`;

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="w-24 shrink-0">
        <div className="text-sm font-semibold leading-tight">{label}</div>
        {sublabel && (
          <div className="text-[0.625rem] leading-tight text-muted-foreground">{sublabel}</div>
        )}
      </div>

      <div className="relative h-7 min-w-0 flex-1 overflow-visible rounded-sm" style={{ backgroundColor: track }}>
        <div
          className="absolute inset-y-0 left-0 rounded-sm"
          style={{ width: `${pct * 100}%`, backgroundColor: colour }}
        />
        {/* Percent label — overlaid at the left, always readable above the fill. */}
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-sm font-bold text-foreground">
          {(pct * 100).toFixed(1)}%
        </span>
        {rightText && (
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[0.625rem] font-medium text-muted-foreground">
            {rightText}
          </span>
        )}

        {amdr && <AmdrBrackets range={amdr} />}
      </div>
    </div>
  );
};

/** Grey corner brackets marking an acceptable range on the track. */
const AmdrBrackets = ({ range }: { range: [number, number] }) => {
  const [min, max] = range;
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  return (
    <>
      <Bracket side="left" at={clamp(min)} />
      <Bracket side="right" at={clamp(max)} />
    </>
  );
};

const Bracket = ({ side, at }: { side: 'left' | 'right'; at: number }) => (
  <span
    aria-hidden
    className={cn(
      'pointer-events-none absolute inset-y-0 border-muted-foreground/50',
      side === 'left' ? 'border-l' : 'border-r',
    )}
    style={{ left: `${at * 100}%` }}
  >
    {/* Top + bottom ticks to form a corner bracket. */}
    <span
      className={cn(
        'absolute top-0 h-px w-1 bg-muted-foreground/50',
        side === 'left' ? 'left-0' : 'right-0',
      )}
    />
    <span
      className={cn(
        'absolute bottom-0 h-px w-1 bg-muted-foreground/50',
        side === 'left' ? 'left-0' : 'right-0',
      )}
    />
  </span>
);
